/**
 * One Zaraz event, qq1_cta_click, for every call-to-action click on the site:
 * links to the quote form (/quick-quote...), to the consult pages
 * (/schedule...) and phone links (tel:).
 *
 * The Zaraz trigger on a[href*="quick-quote"] already sends click_to_qq1, but
 * that event does not say which button was clicked. This one does:
 *
 *   cta_location  where the button sits (list below)
 *   cta_type      "quote", "schedule" or "phone"
 *   cta_text      the button label, at most 60 characters ("phone" for tel:)
 *   cta_dest      the destination path, no query string ("tel" for tel:)
 *   page_path     the page the click happened on
 *
 * cta_location is the nearest data-cta-location attribute, else it is read
 * from the page region. The values:
 *
 *   header       site header and main navigation
 *   hero         the first section of a page
 *   body         page content with no closer mark
 *   inline_blog  inside a blog post, and the InlineQuoteCta block
 *   sticky_bar   the mobile quote bar (the old value "bar" maps to this)
 *   footer       site footer
 *   pricing      pricing blocks
 *   results      results pages
 *   doctor       doctor pages
 *   not_found    the 404 page
 *   form         the Norwood picker block, and the quote and consult pages
 *
 * No phone number, name, email or query string is sent. The name starts with
 * qq1_ because that is the pattern Zaraz forwards to GA4.
 */
import { qqTrack } from "@/lib/qq-diagnostics";

const LOCATIONS = [
  "header",
  "hero",
  "body",
  "inline_blog",
  "sticky_bar",
  "footer",
  "pricing",
  "results",
  "doctor",
  "not_found",
  "form",
] as const;

export type CtaLocation = (typeof LOCATIONS)[number];
export type CtaType = "quote" | "schedule" | "phone";

// Values used before this list existed.
const LEGACY_LOCATIONS: Record<string, CtaLocation> = { bar: "sticky_bar" };

const SITE_HOST = "restorehair.com";
const FORM_PAGE = /^\/(quick-quote|schedule)/;

/** The CTA type and destination path of a link, or null when it is not a CTA. */
export function ctaTarget(href: string, origin: string): { type: CtaType; dest: string } | null {
  if (/^tel:/i.test(href)) return { type: "phone", dest: "tel" };
  let url: URL;
  try {
    url = new URL(href, origin);
  } catch {
    return null;
  }
  // Same site only: the Acuity scheduler is at /schedule.php on another host.
  if (url.origin !== origin && !url.hostname.endsWith(SITE_HOST)) return null;
  if (url.pathname.startsWith("/quick-quote")) return { type: "quote", dest: url.pathname };
  if (url.pathname.startsWith("/schedule")) return { type: "schedule", dest: url.pathname };
  return null;
}

/** Where a CTA sits: the nearest data-cta-location, else the page region. */
export function ctaLocation(el: Element, pathname: string): CtaLocation {
  const tagged = el.closest("[data-cta-location]")?.getAttribute("data-cta-location") ?? "";
  const known = LEGACY_LOCATIONS[tagged] ?? tagged;
  if ((LOCATIONS as readonly string[]).includes(known)) return known as CtaLocation;

  if (el.closest("[data-quote-bar]")) return "sticky_bar";
  if (el.closest("[data-site-header]")) return "header";
  // A <header> or <nav> inside the content (a post title, breadcrumbs) is not
  // the site header.
  const inContent = el.closest("main, article");
  if (!inContent && el.closest("header, nav")) return "header";
  if (el.closest("footer")) return "footer";
  if (el.closest("article")) return "inline_blog";
  if (el.ownerDocument.querySelector("main section")?.contains(el)) return "hero";
  if (FORM_PAGE.test(pathname)) return "form";
  return "body";
}

function ctaText(a: HTMLAnchorElement, type: CtaType): string {
  // The label of a phone link is the number.
  if (type === "phone") return "phone";
  return (a.innerText || a.textContent || "").replace(/\s+/g, " ").trim().slice(0, 60);
}

/**
 * Adds the click listener, once. It listens on document, so it covers every
 * link on every page, including ones rendered later. It runs in the capture
 * phase, reads the link and returns: it does not cancel or delay the click.
 */
export function initCtaTracking(): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as { __rhCtaClickBound?: boolean };
  if (w.__rhCtaClickBound) return;
  w.__rhCtaClickBound = true;
  document.addEventListener(
    "click",
    (event) => {
      try {
        const a = (event.target as Element | null)?.closest?.(
          "a[href]",
        ) as HTMLAnchorElement | null;
        if (!a) return;
        const target = ctaTarget(a.getAttribute("href") ?? "", window.location.origin);
        if (!target) return;
        qqTrack("qq1_cta_click", {
          cta_location: ctaLocation(a, window.location.pathname),
          cta_type: target.type,
          cta_text: ctaText(a, target.type),
          cta_dest: target.dest,
          page_path: window.location.pathname,
        });
      } catch {
        /* tracking must never block the click */
      }
    },
    true,
  );
}
